import * as React from "react";
import { Cpu, CheckCircle, XCircle, Clock } from "react-feather";
import { useAppSelector } from "../../store/hooks";
import { agentHelpers } from "../../mst/kinds/agent.helpers";
import { InternalLink } from "../../newcomponents/table/components/InternalLink";
import { linksOf } from "../../services/cpln";
import { HEALTH_COLORS } from "./utils";
import { Loader } from "../../components/layout/loader";

export const Agents: React.FC = () => {
  const isLoading = useAppSelector((state) => state.health.isLoading);
  const isFetched = useAppSelector((state) => state.health.isFetched);
  const agents = useAppSelector((state) => state.health.agents);
  const agentInfoMap = useAppSelector((state) => state.health.agentInfoMap);

  // Categorize agents
  const categorizedAgents = React.useMemo(() => {
    const active: { agent: typeof agents[0]; lastActiveText: string }[] = [];
    const inactive: { agent: typeof agents[0]; lastActiveText: string }[] = [];

    for (const agent of agents) {
      const info = agentInfoMap[agent.name];
      const isActive = agentHelpers.getIsActive(agent, info);
      const lastActiveText = agentHelpers.getLastActiveText(agent, info);

      if (isActive) {
        active.push({ agent, lastActiveText });
      } else {
        inactive.push({ agent, lastActiveText });
      }
    }

    return { active, inactive };
  }, [agents, agentInfoMap]);

  if (isLoading && !isFetched) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader />
      </div>
    );
  }

  return (
    <div className="pb-16" style={{ maxWidth: 1000 }}>
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-2">
          <Cpu size={20} style={{ color: "var(--color-label)" }} />
          <h1 className="text-xl font-semibold" style={{ color: "var(--color-default)" }}>
            Agent Health
          </h1>
        </div>
        <div className="flex items-center gap-4 text-sm" style={{ color: "var(--color-label)" }}>
          <span className="flex items-center gap-1.5">
            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: HEALTH_COLORS.ready }} />
            {categorizedAgents.active.length} active
          </span>
          {categorizedAgents.inactive.length > 0 && (
            <span className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: HEALTH_COLORS.error }} />
              {categorizedAgents.inactive.length} inactive
            </span>
          )}
        </div>
      </div>

      {agents.length === 0 ? (
        <div
          className="rounded-lg p-8 text-center"
          style={{ backgroundColor: "var(--color-drop)", border: "1px solid var(--color-border)" }}
        >
          <div className="text-sm" style={{ color: "var(--color-label)" }}>
            No agents configured
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Active Agents */}
          {categorizedAgents.active.length > 0 && (
            <div>
              <h2 className="text-sm font-medium mb-3" style={{ color: HEALTH_COLORS.ready }}>
                Active Agents ({categorizedAgents.active.length})
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {categorizedAgents.active.map(({ agent, lastActiveText }) => {
                  const selfLink = linksOf(agent).self;
                  return (
                    <div
                      key={agent.name}
                      className="rounded-lg p-3"
                      style={{
                        backgroundColor: "var(--color-drop)",
                        border: "1px solid var(--color-border)",
                      }}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <CheckCircle size={14} color={HEALTH_COLORS.ready} />
                          {selfLink ? (
                            <InternalLink label={agent.name} to={`/console${selfLink}`} />
                          ) : (
                            <span className="text-sm font-medium" style={{ color: "var(--color-default)" }}>
                              {agent.name}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="text-xs" style={{ color: "var(--color-label)" }}>
                        {lastActiveText}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Inactive Agents */}
          {categorizedAgents.inactive.length > 0 && (
            <div>
              <h2 className="text-sm font-medium mb-3" style={{ color: HEALTH_COLORS.error }}>
                Inactive Agents ({categorizedAgents.inactive.length})
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {categorizedAgents.inactive.map(({ agent, lastActiveText }) => {
                  const selfLink = linksOf(agent).self;
                  return (
                    <div
                      key={agent.name}
                      className="rounded-lg p-3"
                      style={{
                        backgroundColor: "var(--color-drop)",
                        border: `1px solid ${HEALTH_COLORS.error}`,
                      }}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <XCircle size={14} color={HEALTH_COLORS.error} />
                          {selfLink ? (
                            <InternalLink label={agent.name} to={`/console${selfLink}`} />
                          ) : (
                            <span className="text-sm font-medium" style={{ color: "var(--color-default)" }}>
                              {agent.name}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-1 text-xs" style={{ color: "var(--color-label)" }}>
                        <Clock size={12} />
                        {lastActiveText}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
